import { Program } from 'planby';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval } from '@intigral/hooks';

type DateType = string | number | Date;

const PROGRAM_REFRESH = 60000; // 1 minute

/**
 * Enhancing a list of programs by adding a live status to each program and periodically refreshing the live status of
 * the programs.
 * The live status is determined based on the current time and the program's start and end times.
 *
 * @param programsWithoutStatusSet - An array of programs without the live status set.
 * @returns An object containing the updated list of programs with the live status.
 *
 * @remarks
 * - The live status is updated every `PROGRAM_REFRESH` milliseconds.
 * - The hook uses an interval to periodically refresh the live status.
 * - Make sure to pass a valid array of programs with `since` and `till` properties.
 *
 * @example
 * ```tsx
 * const programs = [
 *   { id: 1, name: "Program 1", since: new Date(), till: new Date(Date.now() + 3600000) },
 *   { id: 2, name: "Program 2", since: new Date(Date.now() - 3600000), till: new Date() },
 * ];
 *
 * const { programsWithStatus } = usePrograms(programs);
 *
 * programsWithStatus.forEach((program) => {
 *   console.log(`${program.name} is ${program.isLive ? "live" : "not live"}`);
 * });
 * ```
 */
export default function usePrograms(programsWithoutStatusSet: Program[] = []) {
    const programsRef = useRef<Program[]>(programsWithoutStatusSet);

    useEffect(() => {
        programsRef.current = programsWithoutStatusSet;
    }, [programsWithoutStatusSet]);

    const addLiveStatusToPrograms = useCallback(
        (programs: Program[] = programsRef.current) => {
            return programs.map((program) => {
                const { since, till } = program;

                const status = getLiveStatus(since, till);
                return {
                    ...program,
                    isLive: status,
                };
            });
        },
        [programsWithoutStatusSet.length]
    );

    // const [programsWithStatus, setPrograms] = useState<Program[]>(addLiveStatusToPrograms());
    const [programsWithStatus, setPrograms] = useState<Program[]>([]);

    function getTime(date: DateType) {
        return new Date(date).getTime();
    }

    function getLiveStatus(since: DateType, till: DateType) {
        const nowTime = getTime(new Date());
        const sinceTime = getTime(since);
        const sinceTill = getTime(till);
        return nowTime >= sinceTime && sinceTill > nowTime;
    }

    const { start, stop } = useInterval(function () {
        setPrograms(addLiveStatusToPrograms());
    }, PROGRAM_REFRESH);

    // update live status when programsWithoutStatusSet changes (e.g. when user scrolls and previous programs are added
    // to the list)
    useEffect(() => {
        setPrograms(addLiveStatusToPrograms(programsWithoutStatusSet));
    }, [programsWithoutStatusSet]);

    // useLayoutEffect?
    useEffect(() => {
        start();

        return () => {
            stop();
        };
        // TODO: useCallback(start), useCallback(stop)
    }, []);

    return { programsWithStatus };
}
